// @ai-generated 2026-08-26T12:58:28+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
import { type ApiDynamicExportRun } from '@Model';
import { type EnumMessageLevel, type IApiMessage } from '@synqo/frontend-component-library';
import { describeApiMessage, parseApiMessages } from './parseApiMessages';

/** Anything that persisted its outcome as messages, such as an export run or a ZIP archive. */
export type RunResult = Pick<ApiDynamicExportRun, 'result_details'>;

/**
 * Whether the run has anything to say about how it turned out. Deliberately not a check for the
 * failed state: the backend fills its result field for the outcome, not for failure, so a future
 * partial-success remark shows up without the callers learning about it.
 */
export function hasExportRunResult(run: ApiDynamicExportRun): boolean {
    return exportRunResultMessages(run).length > 0;
}

/**
 * What the run reported, as the messages the backend persisted. Empty for a run from before those
 * were kept, or one whose details are not a list of messages — there is nothing to show either way.
 */
export function exportRunResultMessages(run: RunResult): IApiMessage[] {
    if (!run.result_details) {
        return [];
    }
    try {
        return parseApiMessages(run.result_details);
    } catch {
        return [];
    }
}

/** The result as the lines a person reads: one per reported message. */
export function exportRunResultLines(run: ApiDynamicExportRun): string[] {
    return exportRunResultMessages(run).map(describeApiMessage);
}

/** A reported problem, split into the parts a reader scans separately. */
export interface ExportRunFinding {
    /** What went wrong, without the group's shared opening and without the trailing location. */
    text: string;
    /** The position in the script the message pointed at, e.g. `/rows/3/columns/2`. */
    location?: string;
    level: EnumMessageLevel;
    /** How many identical messages this finding stands for. */
    occurrences: number;
}

/** The findings that share a heading, as the one block a reader takes in at once. */
export interface ExportRunFindingGroup {
    /** The heading these messages are read under, e.g. "Validation Error". */
    title: string;
    /**
     * Whether that heading is the backend's or only stands in for one. A group without a real
     * heading cannot be summarised by it, so nothing may be hidden behind it.
     */
    titled: boolean;
    /** The worst level in the group — what its badge shows. */
    level: EnumMessageLevel;
    /**
     * The opening every finding in the group shared, lifted out of them. A schema check reports
     * forty messages that all start the same way; saying it once is the difference between a
     * readable list and a wall of text.
     */
    sharedPrefix?: string;
    findings: ExportRunFinding[];
    /** How many messages the group stands for, repetitions included. */
    count: number;
}

/** Stands in as a heading for a message the backend sent without a title. */
const UNTITLED_FINDINGS = 'Meldung';

/**
 * The script position a message ends with. The schema check appends the JSON pointer it complained
 * about; recognising only a pointer keeps an ordinary sentence ending in "at ..." intact.
 */
const LOCATION_SUFFIX = /\s+at\s+(\/\S+)$/;

/** A shared opening shorter than this is not worth a line of its own. */
const MIN_SHARED_PREFIX_LENGTH = 8;

/**
 * What the run reported, arranged the way it is read: one block per heading, identical messages
 * folded together, and the wording all of a block's messages share said once instead of per line.
 *
 * @ai-generated 2026-08-28T07:59:23+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
 */
export function exportRunFindingGroups(run: RunResult): ExportRunFindingGroup[] {
    const groups = new Map<string, ExportRunFindingGroup>();

    for (const message of exportRunResultMessages(run)) {
        const titled = Boolean(message.title);
        const title = titled ? message.title : UNTITLED_FINDINGS;
        const group = groups.get(title) ?? { title, titled, level: message.level, findings: [], count: 0 };
        groups.set(title, addFinding(group, message));
    }

    return [...groups.values()].map(hoistSharedPrefix);
}

/**
 * How many messages the run reported per level, worst first — the size of the problem before any of
 * it is read.
 *
 * @ai-generated 2026-08-28T07:59:23+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
 */
export function exportRunLevelCounts(run: RunResult): { level: EnumMessageLevel; count: number }[] {
    const counts = new Map<EnumMessageLevel, number>();

    for (const message of exportRunResultMessages(run)) {
        counts.set(message.level, (counts.get(message.level) ?? 0) + 1);
    }

    return [...counts.entries()]
        .map(([level, count]) => ({ level, count }))
        .sort((left, right) => right.level - left.level);
}

/** Folds a message into its group, counting a repetition rather than listing it twice. */
function addFinding(group: ExportRunFindingGroup, message: IApiMessage): ExportRunFindingGroup {
    const [, location] = LOCATION_SUFFIX.exec(message.message) ?? [];
    const text = location ? message.message.replace(LOCATION_SUFFIX, '') : message.message;
    const known = group.findings.find((finding) => finding.text === text && finding.location === location);

    if (known) {
        known.occurrences += 1;
    } else {
        group.findings.push({ text, location, level: message.level, occurrences: 1 });
    }

    return {
        ...group,
        level: message.level > group.level ? message.level : group.level,
        count: group.count + 1,
    };
}

/** Moves the opening all of a group's findings share onto the group itself. */
function hoistSharedPrefix(group: ExportRunFindingGroup): ExportRunFindingGroup {
    const prefix = sharedFindingPrefix(group.findings);
    if (!prefix) {
        return group;
    }

    return {
        ...group,
        sharedPrefix: prefix.slice(0, -2),
        findings: group.findings.map((finding) => ({ ...finding, text: finding.text.slice(prefix.length) })),
    };
}

/**
 * The `": "`-terminated opening every finding begins with, or nothing when the findings do not share
 * one long enough to be worth hoisting — or when hoisting it would leave a finding with no text.
 */
function sharedFindingPrefix(findings: ExportRunFinding[]): string | undefined {
    if (findings.length < 2) {
        return undefined;
    }

    let common = findings[0].text;
    for (const finding of findings.slice(1)) {
        while (common && !finding.text.startsWith(common)) {
            common = common.slice(0, -1);
        }
    }

    const boundary = common.lastIndexOf(': ');
    if (boundary < 0) {
        return undefined;
    }

    const prefix = common.slice(0, boundary + 2);
    if (prefix.length < MIN_SHARED_PREFIX_LENGTH || findings.some((finding) => finding.text.length <= prefix.length)) {
        return undefined;
    }

    return prefix;
}
